import { alpha, Components, Theme } from '@mui/material/styles';

import { encodeSvg } from '@/utils/svg';
import { getPrimaryButtonStyles, getSecondaryButtonStyles } from './buttonStyles';

declare module '@mui/material/Button' {
  interface ButtonOwnProps {
    containerbackground?: 'light' | 'dark';
    arrowIcon?: 'left' | 'right' | 'arrow-only' | 'left-chevron';
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    // Turn off the built in variants.
    contained: false;
    outlined: false;
    text: false;

    // Turn on our variants.
    primary: true;
    secondary: true;
    tertiary: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    // Turn off all the color overrides because our branded buttons don't allow color changes.
    primary: false;
    secondary: false;
    inherit: false;
    error: false;
    warning: false;
    info: false;
    success: false;
  }
}

const makeUncircledRightArrowSvg = (
  arrowColor: string
) => `<svg width="100%" height="100%" viewBox="0 0 25 25" xmlns="http://www.w3.org/2000/svg">
<path fill-rule="evenodd" clip-rule="evenodd" d="M13.7797 6.41652L19.685 11.8486H1.83359V13.4838H19.6843L13.7809 18.9147L14.73 20.1667L22.8336 12.6458L14.7298 5.16674L13.7797 6.41652Z" fill="${arrowColor}"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M13.7809 18.9147L19.6843 13.4838H1.83359V11.8486H19.685L13.7797 6.41652L14.7298 5.16674L22.8336 12.6458L14.73 20.1667L13.7809 18.9147ZM19.3004 11.6986L13.5777 6.43446L14.7102 4.94458L23.0544 12.6455L14.7106 20.3894L13.579 18.8966L19.2997 13.6338H1.68359V11.6986H19.3004Z" fill="${arrowColor}"/>
</svg>`;

const makeCircledRightArrowSvg = (
  arrowColor: string,
  backgroundColor: string
) => `<svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<g clip-path="url(#clip0_2765_99)">
<rect width="100%" height="100%" rx="12" fill="${backgroundColor}"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M12.9645 7.8331L16.9013 11.4545H5.00039V12.5446H16.9008L12.9653 16.1652L13.598 16.9999L19.0004 11.986L13.5978 6.99991L12.9645 7.8331Z" fill="${arrowColor}"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M12.9653 16.1652L16.9008 12.5446H5.00039V11.4545H16.9013L12.9645 7.8331L13.5978 6.99991L19.0004 11.986L13.598 16.9999L12.9653 16.1652ZM16.6449 11.3545L12.8298 7.84506L13.5848 6.85181L19.1476 11.9858L13.585 17.1484L12.8307 16.1532L16.6444 12.6446H4.90039V11.3545H16.6449Z" fill="${arrowColor}"/>
</g>
<defs>
<clipPath id="clip0_2765_99">
<rect width="24" height="24" rx="12" fill="white"/>
</clipPath>
</defs>
</svg>
`;

const makeLeftChevronSvg = (
  color: string
) => `<svg width="100%" height="100%" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M15.743 8.07675L14.333 6.66675L8.33301 12.6667L14.333 18.6667L15.743 17.2567L11.163 12.6667L15.743 8.07675Z" fill="${color}"/>
</svg>
`;

export default function MuiButton(theme: Theme): Components['MuiButton'] {
  const arrowLightBackground = encodeSvg(makeUncircledRightArrowSvg(theme.palette.taiga.main));
  const arrowDarkBackground = encodeSvg(makeUncircledRightArrowSvg(theme.palette.sand.main));
  const circledArrow = encodeSvg(
    makeCircledRightArrowSvg(theme.palette.taiga.main, theme.palette.desert.main)
  );
  const leftChevronLightBackground = encodeSvg(makeLeftChevronSvg(theme.palette.taiga.main));
  const leftChevronDarkBackground = encodeSvg(makeLeftChevronSvg(theme.palette.sand.main));

  return {
    defaultProps: {
      variant: 'secondary',
      size: 'small',
      disableFocusRipple: true, // Turning this off because the ripple looks weird with our branded buttons.
      containerbackground: 'light',
    },
    styleOverrides: {
      root: {
        color: theme.palette.taiga.main,
        fontWeight: 900,
        transition: 'all .5s ease-in-out',
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        borderRadius: 9999,
        '&.Mui-focusVisible': {
          outline: 'none',
          boxShadow: `0 0 0 4px ${alpha(theme.palette.desertTint.main, 0.24)}`,
        },
        display: 'flex',
        gap: '4px',
        position: 'relative',
        whiteSpace: 'nowrap',
      },
    },
    variants: [
      // Large button styles.
      {
        props: { size: 'large' },
        style: {
          fontSize: '16px',
          lineHeight: '24px',
          letterSpacing: '0.8px',
          padding: '11px 32px 13px 32px',
          maxHeight: '42px',
        },
      },
      {
        props: { size: 'large', variant: 'tertiary' },
        style: {
          padding: '0 32px 2px 32px',
          borderBottomWidth: '2px',
        },
      },
      {
        props: { size: 'large', arrowIcon: 'left' },
        style: {
          '&::before': {
            content: arrowLightBackground,
            width: 20,
            height: 20,
            position: 'relative',
            top: 1,
          },
          paddingLeft: '26px',
          borderBottomWidth: 0,
        },
      },
      {
        props: {
          size: 'large',
          arrowIcon: 'left',
          containerbackground: 'dark',
          variant: 'secondary',
        },
        style: {
          '&::before': {
            content: arrowDarkBackground,
          },
        },
      },
      {
        props: { size: 'large', arrowIcon: 'right' },
        style: {
          '&::after': {
            content: arrowLightBackground,
            width: 20,
            height: 20,
            position: 'relative',
            top: 1,
          },
          paddingRight: '26px',
          borderBottomWidth: 0,
        },
      },
      {
        props: {
          size: 'large',
          arrowIcon: 'right',
          containerbackground: 'dark',
          variant: 'secondary',
        },
        style: {
          '&::after': {
            content: arrowDarkBackground,
          },
        },
      },
      {
        props: { size: 'large', arrowIcon: 'arrow-only' },
        style: {
          '&::after': {
            content: arrowLightBackground,
            position: 'absolute',
            width: 20,
            height: 20,
          },
          height: '48px',
          width: '48px',
          maxHeight: '48px',
          maxWidth: '48px',
          minHeight: '48px',
          minWidth: '48px',
          padding: '0px',
          fontSize: 0,
          lineHeight: 0,
          borderBottomWidth: 0,
        },
      },
      {
        props: {
          size: 'large',
          arrowIcon: 'arrow-only',
          containerbackground: 'dark',
          variant: 'secondary',
        },
        style: {
          '&::after': {
            content: arrowDarkBackground,
          },
        },
      },
      {
        props: { size: 'large', arrowIcon: 'left', variant: 'tertiary' },
        style: {
          '&::before': {
            content: circledArrow,
            width: 20,
            height: 20,
            marginRight: 6,
          },
        },
      },
      {
        props: { size: 'large', arrowIcon: 'right', variant: 'tertiary' },
        style: {
          '&::after': {
            content: circledArrow,
            width: 20,
            height: 20,
            marginLeft: 4,
          },
        },
      },
      {
        props: { size: 'large', arrowIcon: 'arrow-only', variant: 'tertiary' },
        style: {
          '&::after': {
            content: circledArrow,
            position: 'absolute',
            width: 20,
            height: 20,
          },
        },
      },
      {
        props: {
          size: 'large',
          arrowIcon: 'left-chevron',
          variant: 'tertiary',
        },
        style: {
          '&::before': {
            content: leftChevronLightBackground,
            width: 20,
            height: 20,
            position: 'relative',
            top: 2,
          },
          borderBottomWidth: 0,
        },
      },
      {
        props: {
          size: 'large',
          arrowIcon: 'left-chevron',
          variant: 'tertiary',
          containerbackground: 'dark',
        },
        style: {
          '&::before': {
            content: leftChevronDarkBackground,
          },
        },
      },

      // Medium button styles.
      {
        props: { size: 'medium' },
        style: {
          fontSize: '15px',
          lineHeight: '20px',
          letterSpacing: '0.8px',
          padding: '9px 24px 10px 24px',
          maxHeight: '41px',
        },
      },
      {
        props: { size: 'medium', variant: 'tertiary' },
        style: {
          padding: '0 24px 2px 24px',
          borderBottomWidth: '1.5px',
        },
      },
      {
        props: { size: 'medium', arrowIcon: 'left' },
        style: {
          '&::before': {
            content: arrowLightBackground,
            width: 18,
            height: 18,
            position: 'relative',
            top: 1,
          },
          paddingLeft: '20px',
          borderBottomWidth: 0,
        },
      },
      {
        props: {
          size: 'medium',
          arrowIcon: 'left',
          containerbackground: 'dark',
          variant: 'secondary',
        },
        style: {
          '&::before': {
            content: arrowDarkBackground,
          },
        },
      },
      {
        props: { size: 'medium', arrowIcon: 'right' },
        style: {
          '&::after': {
            content: arrowLightBackground,
            width: 18,
            height: 18,
            position: 'relative',
            top: 1,
          },
          paddingRight: '20px',
          borderBottomWidth: 0,
        },
      },
      {
        props: {
          size: 'medium',
          arrowIcon: 'right',
          containerbackground: 'dark',
          variant: 'secondary',
        },
        style: {
          '&::after': {
            content: arrowDarkBackground,
          },
        },
      },
      {
        props: { size: 'medium', arrowIcon: 'arrow-only' },
        style: {
          '&::after': {
            content: arrowLightBackground,
            position: 'absolute',
            width: 18,
            height: 18,
          },
          height: '40px',
          width: '40px',
          maxHeight: '40px',
          maxWidth: '40px',
          minHeight: '40px',
          minWidth: '40px',
          padding: '0px',
          fontSize: 0,
          lineHeight: 0,
          borderBottomWidth: 0,
        },
      },
      {
        props: {
          size: 'medium',
          arrowIcon: 'arrow-only',
          containerbackground: 'dark',
          variant: 'secondary',
        },
        style: {
          '&::after': {
            content: arrowDarkBackground,
          },
        },
      },
      {
        props: { size: 'medium', arrowIcon: 'left', variant: 'tertiary' },
        style: {
          '&::before': {
            content: circledArrow,
            width: 18,
            height: 18,
            marginRight: 4,
          },
        },
      },
      {
        props: { size: 'medium', arrowIcon: 'right', variant: 'tertiary' },
        style: {
          '&::after': {
            content: circledArrow,
            width: 18,
            height: 18,
            marginLeft: 4,
          },
        },
      },
      {
        props: { size: 'medium', arrowIcon: 'arrow-only', variant: 'tertiary' },
        style: {
          '&::after': {
            content: circledArrow,
            position: 'absolute',
            width: 18,
            height: 18,
          },
        },
      },
      {
        props: {
          size: 'medium',
          arrowIcon: 'left-chevron',
          variant: 'tertiary',
        },
        style: {
          '&::before': {
            content: leftChevronLightBackground,
            width: 18,
            height: 18,
            position: 'relative',
            top: 1,
          },
          borderBottomWidth: 0,
        },
      },
      {
        props: {
          size: 'medium',
          arrowIcon: 'left-chevron',
          variant: 'tertiary',
          containerbackground: 'dark',
        },
        style: {
          '&::before': {
            content: leftChevronDarkBackground,
          },
        },
      },

      // Small button styles.
      {
        props: { size: 'small' },
        style: {
          fontSize: '14px',
          lineHeight: '16px',
          letterSpacing: '0.8px',
          padding: '7px 16px 8px 16px',
          maxHeight: '35px',
        },
      },
      {
        props: { size: 'small', variant: 'tertiary' },
        style: {
          padding: '0 16px 2px 16px',
          borderBottomWidth: '1px',
        },
      },
      {
        props: { size: 'small', arrowIcon: 'left' },
        style: {
          '&::before': {
            content: arrowLightBackground,
            width: 16,
            height: 16,
            position: 'relative',
            top: 1,
          },
          paddingLeft: '14px',
          borderBottomWidth: 0,
        },
      },
      {
        props: {
          size: 'small',
          arrowIcon: 'left',
          containerbackground: 'dark',
          variant: 'secondary',
        },
        style: {
          '&::before': {
            content: arrowDarkBackground,
          },
        },
      },
      {
        props: { size: 'small', arrowIcon: 'right' },
        style: {
          '&::after': {
            content: arrowLightBackground,
            width: 16,
            height: 16,
            position: 'relative',
            top: 1,
          },
          paddingRight: '14px',
          borderBottomWidth: 0,
        },
      },
      {
        props: {
          size: 'small',
          arrowIcon: 'right',
          containerbackground: 'dark',
          variant: 'secondary',
        },
        style: {
          '&::after': {
            content: arrowDarkBackground,
          },
        },
      },
      {
        props: { size: 'small', arrowIcon: 'arrow-only' },
        style: {
          '&::after': {
            content: arrowLightBackground,
            position: 'absolute',
            width: 16,
            height: 16,
          },
          height: '32px',
          width: '32px',
          maxHeight: '32px',
          maxWidth: '32px',
          minHeight: '32px',
          minWidth: '32px',
          padding: '0px',
          fontSize: 0,
          lineHeight: 0,
          borderBottomWidth: 0,
        },
      },
      {
        props: {
          size: 'small',
          arrowIcon: 'arrow-only',
          containerbackground: 'dark',
          variant: 'secondary',
        },
        style: {
          '&::after': {
            content: arrowDarkBackground,
          },
        },
      },
      {
        props: { size: 'small', arrowIcon: 'left', variant: 'tertiary' },
        style: {
          '&::before': {
            content: circledArrow,
            width: 16,
            height: 16,
            marginRight: 4,
          },
        },
      },
      {
        props: { size: 'small', arrowIcon: 'right', variant: 'tertiary' },
        style: {
          '&::after': {
            content: circledArrow,
            width: 16,
            height: 16,
            marginLeft: 4,
          },
        },
      },
      {
        props: { size: 'small', arrowIcon: 'arrow-only', variant: 'tertiary' },
        style: {
          '&::after': {
            content: circledArrow,
            position: 'absolute',
            width: 16,
            height: 16,
          },
        },
      },
      {
        props: {
          size: 'small',
          arrowIcon: 'left-chevron',
          variant: 'tertiary',
        },
        style: {
          '&::before': {
            content: leftChevronLightBackground,
            width: 16,
            height: 16,
            position: 'relative',
            top: 0.5,
          },
          borderBottomWidth: 0,
        },
      },
      {
        props: {
          size: 'small',
          arrowIcon: 'left-chevron',
          variant: 'tertiary',
          containerbackground: 'dark',
        },
        style: {
          '&::before': {
            content: leftChevronDarkBackground,
          },
        },
      },

      // Set the text color depending on whether the background is light or dark.
      {
        props: { containerbackground: 'light' },
        style: {
          color: theme.palette.taiga.main,
        },
      },
      {
        props: { containerbackground: 'dark' },
        style: {
          color: theme.palette.sand.main,
        },
      },

      // Bulk of the variant styles.
      {
        props: { variant: 'primary' },
        style: getPrimaryButtonStyles(theme),
      },
      {
        props: { variant: 'secondary' },
        style: getSecondaryButtonStyles(theme),
      },
      {
        props: { variant: 'tertiary' },
        style: {
          backgroundColor: 'transparent',
          borderBottomStyle: 'solid',
          borderBottomColor: theme.palette.desert.main,
          borderRadius: 0,
          paddingLeft: 0,
          paddingRight: 0,
          '& .MuiButton-startIcon, & .MuiButton-endIcon': {
            marginLeft: 0,
            marginRight: 0,
          },
          '&:hover': {
            borderBottomColor: theme.palette.desertTint.main,
            color: theme.palette.taigaTint1.main,
          },
          '&.Mui-focusVisible': {
            borderBottomColor: theme.palette.desertTint.main,
            boxShadow: `0 0 0 4px ${alpha(theme.palette.desertTint.main, 0.24)}`,
          },
          '&.Mui-disabled': { color: theme.palette.taigaTint1.main },
        },
      },
    ],
  };
}
